import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  AlertTriangle,
  Activity,
  Heart,
  Wind,
  Brain,
  Bone,
  Download,
  RotateCcw,
  Clock,
  Calendar,
  FileText,
  Droplet,
  TrendingUp,
  Sparkles,
  Loader2
} from 'lucide-react';

import { API_URL } from '../config';

export default function TraumaSeverePage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('trauma-severe-form');
      return saved ? JSON.parse(saved) : {
        contexte: '',
        hemorragie: {
          controlee: null,
          situations: []
        },
        respi: {
          oxygene: null,
          suspicionPNO: null,
          exsufflation: null,
          intubation: null
        },
        hemodynamique: {
          pas: '',
          pad: '',
          fc: '',
          pam: '',
          shockIndex: '',
          siModifie: '',
          hemocue: '',
          fastEcho: null
        },
        neuro: {
          glasgow: 15,
          pupillesSymetriques: null,
          mobilisation4Membres: null,
          embarrure: null
        },
        orthopedie: {
          fractureOuverte: null,
          deficitVasculoNerveux: null,
          arrachementMembre: null
        },
        redFlags: 0,
        transport: {
          delai: 0,
          dateHeureArrivee: ''
        }
      };
    } catch {
      return {
        contexte: '',
        hemorragie: { controlee: null, situations: [] },
        respi: { oxygene: null, suspicionPNO: null, exsufflation: null, intubation: null },
        hemodynamique: { pas: '', pad: '', fc: '', pam: '', shockIndex: '', siModifie: '', hemocue: '', fastEcho: null },
        neuro: { glasgow: 15, pupillesSymetriques: null, mobilisation4Membres: null, embarrure: null },
        orthopedie: { fractureOuverte: null, deficitVasculoNerveux: null, arrachementMembre: null },
        redFlags: 0,
        transport: { delai: 0, dateHeureArrivee: '' }
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('trauma-severe-form', JSON.stringify(formData));
    
    // Calculate derived values
    if (formData.hemodynamique.fc && formData.hemodynamique.pas) {
      const si = (parseFloat(formData.hemodynamique.fc) / parseFloat(formData.hemodynamique.pas)).toFixed(2);
      if (!isNaN(si)) {
        setFormData(prev => ({
          ...prev,
          hemodynamique: { ...prev.hemodynamique, shockIndex: si }
        }));
      }
    }
    
    if (formData.hemodynamique.fc && formData.hemodynamique.pam) {
      const siMod = (parseFloat(formData.hemodynamique.fc) / parseFloat(formData.hemodynamique.pam)).toFixed(2);
      if (!isNaN(siMod)) {
        setFormData(prev => ({
          ...prev,
          hemodynamique: { ...prev.hemodynamique, siModifie: siMod }
        }));
      }
    }
    
    // Calculate PAM if PAS and PAD are filled
    if (formData.hemodynamique.pas && formData.hemodynamique.pad) {
      const pam = Math.round(parseFloat(formData.hemodynamique.pad) + (parseFloat(formData.hemodynamique.pas) - parseFloat(formData.hemodynamique.pad)) / 3);
      if (!isNaN(pam)) {
        setFormData(prev => ({
          ...prev,
          hemodynamique: { ...prev.hemodynamique, pam: pam.toString() }
        }));
      }
    }
  }, [formData.hemodynamique.fc, formData.hemodynamique.pas, formData.hemodynamique.pad]);

  // Calculate Red Flags automatically
  useEffect(() => {
    let count = 0;
    
    // 1. Bassin instable
    if (formData.hemorragie.situations.includes('Bassin instable')) {
      count++;
    }
    
    // 2. PAS < 90 mmHg
    if (formData.hemodynamique.pas && parseFloat(formData.hemodynamique.pas) < 90) {
      count++;
    }
    
    // 3. FC > 120 bpm
    if (formData.hemodynamique.fc && parseFloat(formData.hemodynamique.fc) > 120) {
      count++;
    }
    
    // 4. PAM <= 70 mmHg
    if (formData.hemodynamique.pam && parseFloat(formData.hemodynamique.pam) <= 70) {
      count++;
    }
    
    // 5. Intubation = Oui
    if (formData.respi.intubation === true) {
      count++;
    }
    
    // 6. Hémoglobine <= 13 g/dL
    if (formData.hemodynamique.hemocue && parseFloat(formData.hemodynamique.hemocue) <= 13) {
      count++;
    }
    
    // Update red flags count
    if (count !== formData.redFlags) {
      setFormData(prev => ({ ...prev, redFlags: count }));
    }
  }, [
    formData.hemorragie.situations,
    formData.hemodynamique.pas,
    formData.hemodynamique.fc,
    formData.hemodynamique.pam,
    formData.hemodynamique.hemocue,
    formData.respi.intubation,
    formData.redFlags
  ]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const toggleCheckbox = (section, field, value = null) => {
    if (value !== null) {
      updateField(section, field, value);
    } else {
      updateField(section, field, !formData[section][field]);
    }
  };

  const toggleSituation = (situation) => {
    setFormData(prev => ({
      ...prev,
      hemorragie: {
        ...prev.hemorragie,
        situations: prev.hemorragie.situations.includes(situation)
          ? prev.hemorragie.situations.filter(s => s !== situation)
          : [...prev.hemorragie.situations, situation]
      }
    }));
  };

  const handleReset = () => {
    if (window.confirm('Réinitialiser tout le bilan ?')) {
      const emptyForm = {
        contexte: '',
        hemorragie: { controlee: null, situations: [] },
        respi: { oxygene: null, suspicionPNO: null, exsufflation: null, intubation: null },
        hemodynamique: { pas: '', pad: '', fc: '', pam: '', shockIndex: '', siModifie: '', hemocue: '', fastEcho: null },
        neuro: { glasgow: 15, pupillesSymetriques: null, mobilisation4Membres: null, embarrure: null },
        orthopedie: { fractureOuverte: null, deficitVasculoNerveux: null, arrachementMembre: null },
        redFlags: 0,
        transport: { delai: 0, dateHeureArrivee: '' }
      };
      setFormData(emptyForm);
      localStorage.removeItem('trauma-severe-form');
      toast.success('Bilan réinitialisé');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF en cours...');
      
      const element = printRef.current;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Bilan-Trauma-Severe-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success('PDF exporté avec succès');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-trauma`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'analyse');
      }

      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      console.error('Error analyzing trauma:', error);
      toast.error('Erreur lors de l\'analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const situationsHemorragiques = [
    "Trauma pénétrant",
    "Cardiac box",
    "Plaie hémorragique",
    "Bassin instable",
    "Trauma abdo",
    "Anticoagulants ou AAP"
  ];

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 max-w-6xl mx-auto" data-testid="trauma-severe-page">
      {/* Header */}
      <div className="space-y-3 print:hidden">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-600/20 flex items-center justify-center border border-red-500/30 flex-shrink-0">
              <Activity className="w-5 h-5 md:w-6 md:h-6 text-red-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-[hsl(var(--text))] break-words">
                Bilan Traumatisé Sévère
              </h1>
              <p className="text-xs md:text-sm text-[hsl(var(--text-dim))]">
                En régulation pré-hospitalière
              </p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleAnalyzeAI}
              disabled={isAnalyzing}
              variant="default"
              size="sm"
              className="text-xs md:text-sm bg-purple-400/20 text-purple-300 border border-purple-400/30 hover:bg-purple-400/30 disabled:opacity-50"
              data-testid="analyze-ai-btn"
            >
              {isAnalyzing ? (
                <Loader2 className="w-3.5 h-3.5 md:w-4 md:h-4 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4" />
              )}
              <span className="ml-1 hidden sm:inline">Analyser IA</span>
              <span className="ml-1 sm:hidden">IA</span>
            </Button>
            <Button
              onClick={handleReset}
              variant="outline"
              size="sm"
              className="text-xs md:text-sm text-amber-300 border-amber-400/30 hover:bg-amber-400/10"
              data-testid="reset-trauma-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span className="ml-1 hidden sm:inline">Reset</span>
            </Button>
            <Button
              onClick={handleExportPDF}
              variant="default"
              size="sm"
              className="text-xs md:text-sm bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/30"
              data-testid="export-trauma-pdf-btn"
            >
              <Download className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span className="ml-1 hidden sm:inline">PDF</span>
            </Button>
          </div>
        </div>
        
        <Alert className="bg-red-400/10 border-red-400/30">
          <AlertTriangle className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-400" />
          <AlertDescription className="text-xs md:text-sm text-[hsl(var(--text))]">
            <strong>Évaluation initiale :</strong> Remplir rapidement tous les champs critiques
          </AlertDescription>
        </Alert>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold text-center mb-2">Bilan médical du traumatisé sévère en régulation</h1>
        <p className="text-center text-sm text-gray-600">Date: {new Date().toLocaleDateString('fr-FR')} - {new Date().toLocaleTimeString('fr-FR')}</p>
      </div>

      {/* Form Content */}
      <div ref={printRef} className="space-y-3 md:space-y-4">
        {/* CONTEXTE */}
        <Card className="border-[hsl(var(--border))] print:border-gray-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-gray-100 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-cyan-300 print:text-gray-800 flex items-center gap-2">
              <FileText className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              CONTEXTE
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4">
            <Textarea
              placeholder="Éléments de contexte SUCCINCTS..."
              value={formData.contexte}
              onChange={(e) => setFormData(prev => ({ ...prev, contexte: e.target.value }))}
              className="min-h-[80px] md:min-h-[100px] bg-[hsl(var(--bg-elev))] print:bg-white text-xs md:text-sm resize-none"
              data-testid="contexte-input"
            />
          </CardContent>
        </Card>

        {/* Grid for HÉMORRAGIE, RESPI, HÉMODYNAMIQUE */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
          {/* HÉMORRAGIE */}
          <Card className="border-red-400/30 bg-red-400/5 print:bg-red-50 print:border-red-300 print:break-inside-avoid">
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-red-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-red-300 print:text-red-800 flex items-center gap-2">
                <Droplet className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                HÉMORRAGIE
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4 space-y-3">
              {/* Contrôlée */}
              <div>
                <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 mb-2">Contrôlée</div>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.hemorragie.controlee === true}
                      onCheckedChange={() => toggleCheckbox('hemorragie', 'controlee', true)}
                      className="print:border-gray-400"
                    />
                    <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">oui</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.hemorragie.controlee === false}
                      onCheckedChange={() => toggleCheckbox('hemorragie', 'controlee', false)}
                      className="print:border-gray-400"
                    />
                    <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">non</span>
                  </label>
                </div>
              </div>

              {/* Situation clinique */}
              <div>
                <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 mb-2">Situation clinique</div>
                <div className="space-y-1.5">
                  {situationsHemorragiques.map((situation) => (
                    <label key={situation} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={formData.hemorragie.situations.includes(situation)}
                        onCheckedChange={() => toggleSituation(situation)}
                        className="print:border-gray-400"
                      />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">{situation}</span>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* RESPI */}
          <Card className="border-blue-400/30 bg-blue-400/5 print:bg-blue-50 print:border-blue-300 print:break-inside-avoid">
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-blue-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-blue-300 print:text-blue-800 flex items-center gap-2">
                <Wind className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                RESPI
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4 space-y-3">
              {[
                { key: 'oxygene', label: 'Oxygène' },
                { key: 'suspicionPNO', label: 'Suspicion de PNO' },
                { key: 'exsufflation', label: 'Exsufflation' },
                { key: 'intubation', label: 'Intubation O.T.' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 mb-1.5">{label}</div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={formData.respi[key] === true}
                        onCheckedChange={() => toggleCheckbox('respi', key, true)}
                        className="print:border-gray-400"
                      />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">oui</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={formData.respi[key] === false}
                        onCheckedChange={() => toggleCheckbox('respi', key, false)}
                        className="print:border-gray-400"
                      />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">non</span>
                    </label>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* HÉMODYNAMIQUE */}
          <Card className="border-purple-400/30 bg-purple-400/5 print:bg-purple-50 print:border-purple-300 print:break-inside-avoid">
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-purple-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-purple-300 print:text-purple-800 flex items-center gap-2">
                <Heart className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                HÉMODYNAMIQUE
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4 space-y-2 md:space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-1 block">PAS</label>
                  <Input
                    type="number"
                    placeholder="mmHg"
                    value={formData.hemodynamique.pas}
                    onChange={(e) => updateField('hemodynamique', 'pas', e.target.value)}
                    className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg-elev))] print:bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-1 block">PAD</label>
                  <Input
                    type="number"
                    placeholder="mmHg"
                    value={formData.hemodynamique.pad}
                    onChange={(e) => updateField('hemodynamique', 'pad', e.target.value)}
                    className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg-elev))] print:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-1 block">Fréq. card.</label>
                <Input
                  type="number"
                  placeholder="bpm"
                  value={formData.hemodynamique.fc}
                  onChange={(e) => updateField('hemodynamique', 'fc', e.target.value)}
                  className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg-elev))] print:bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-1 block">PAM</label>
                  <Input
                    type="number"
                    value={formData.hemodynamique.pam}
                    readOnly
                    className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg))] print:bg-gray-100 font-mono text-cyan-300 print:text-gray-900"
                    placeholder="Auto"
                  />
                  <div className="text-[10px] text-[hsl(var(--text-dim))] print:text-gray-500 mt-0.5">FC/PAS</div>
                </div>
                <div>
                  <label className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-1 block">Shock Index</label>
                  <Input
                    type="text"
                    value={formData.hemodynamique.shockIndex}
                    readOnly
                    className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg))] print:bg-gray-100 font-mono text-cyan-300 print:text-gray-900"
                    placeholder="Auto"
                  />
                  <div className="text-[10px] text-[hsl(var(--text-dim))] print:text-gray-500 mt-0.5">FC/PAS</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-1 block">S.I. modifié</label>
                  <Input
                    type="text"
                    value={formData.hemodynamique.siModifie}
                    readOnly
                    className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg))] print:bg-gray-100 font-mono text-cyan-300 print:text-gray-900"
                    placeholder="Auto"
                  />
                  <div className="text-[10px] text-[hsl(var(--text-dim))] print:text-gray-500 mt-0.5">FC/PAM</div>
                </div>
                <div>
                  <label className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-1 block">Hémocue</label>
                  <Input
                    type="number"
                    placeholder="si dispo"
                    value={formData.hemodynamique.hemocue}
                    onChange={(e) => updateField('hemodynamique', 'hemocue', e.target.value)}
                    className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg-elev))] print:bg-white"
                  />
                </div>
              </div>

              <div>
                <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 mb-1.5">Fast Echo positive</div>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.hemodynamique.fastEcho === true}
                      onCheckedChange={() => toggleCheckbox('hemodynamique', 'fastEcho', true)}
                      className="print:border-gray-400"
                    />
                    <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">oui</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.hemodynamique.fastEcho === false}
                      onCheckedChange={() => toggleCheckbox('hemodynamique', 'fastEcho', false)}
                      className="print:border-gray-400"
                    />
                    <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">non</span>
                  </label>
                </div>
                <div className="text-[10px] text-[hsl(var(--text-dim))] print:text-gray-500 mt-1">si disponible</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Grid for NEURO and ORTHOPÉDIE */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {/* NEURO */}
          <Card className="border-green-400/30 bg-green-400/5 print:bg-green-50 print:border-green-300 print:break-inside-avoid">
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-green-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-green-300 print:text-green-800 flex items-center gap-2">
                <Brain className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                NEURO
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4 space-y-3">
              {/* Glasgow Score */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900">Glasgow score</label>
                  <Badge className="bg-green-500/20 text-green-300 print:bg-green-200 print:text-green-900 font-mono text-sm md:text-base">
                    {formData.neuro.glasgow}
                  </Badge>
                </div>
                <Slider
                  value={[formData.neuro.glasgow]}
                  onValueChange={([value]) => updateField('neuro', 'glasgow', value)}
                  min={3}
                  max={15}
                  step={1}
                  className="print:hidden"
                />
              </div>

              {/* Autres paramètres neuro */}
              {[
                { key: 'pupillesSymetriques', label: 'Pupilles symétriques' },
                { key: 'mobilisation4Membres', label: 'Mobilisation des 4 membres' },
                { key: 'embarrure', label: 'Embarrure clinique' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 mb-1.5">{label}</div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={formData.neuro[key] === true}
                        onCheckedChange={() => toggleCheckbox('neuro', key, true)}
                        className="print:border-gray-400"
                      />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">oui</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={formData.neuro[key] === false}
                        onCheckedChange={() => toggleCheckbox('neuro', key, false)}
                        className="print:border-gray-400"
                      />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">non</span>
                    </label>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* ORTHOPÉDIE */}
          <Card className="border-orange-400/30 bg-orange-400/5 print:bg-orange-50 print:border-orange-300 print:break-inside-avoid">
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-orange-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-orange-300 print:text-orange-800 flex items-center gap-2">
                <Bone className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                ORTHOPÉDIE
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4 space-y-3">
              {[
                { key: 'fractureOuverte', label: 'Fracture ouverte' },
                { key: 'deficitVasculoNerveux', label: 'Déficit vasculo-nerveux' },
                { key: 'arrachementMembre', label: 'Arrachement de membre' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 mb-1.5">{label}</div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={formData.orthopedie[key] === true}
                        onCheckedChange={() => toggleCheckbox('orthopedie', key, true)}
                        className="print:border-gray-400"
                      />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">oui</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={formData.orthopedie[key] === false}
                        onCheckedChange={() => toggleCheckbox('orthopedie', key, false)}
                        className="print:border-gray-400"
                      />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">non</span>
                    </label>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* RED FLAGS & TRANSPORT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {/* RED FLAGS */}
          <Card className={`border-red-500/30 print:border-red-400 print:break-inside-avoid ${formData.redFlags > 0 ? 'bg-red-500/10 animate-pulse' : 'bg-red-500/5'}`}>
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-red-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-red-300 print:text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                Red Flags
                {formData.redFlags > 0 && (
                  <Badge className="bg-red-500 text-white animate-pulse ml-auto">
                    ALERTE
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp className={`w-8 h-8 md:w-10 md:h-10 ${formData.redFlags > 0 ? 'text-red-500' : 'text-red-400'} print:text-red-600`} />
                <div className={`h-12 md:h-14 flex items-center justify-center flex-1 text-3xl md:text-4xl font-bold font-mono rounded-lg ${
                  formData.redFlags === 0 ? 'bg-green-500/20 text-green-300 print:bg-green-100 print:text-green-800' :
                  formData.redFlags <= 2 ? 'bg-amber-500/20 text-amber-300 print:bg-amber-100 print:text-amber-800' :
                  'bg-red-500/30 text-red-300 print:bg-red-200 print:text-red-900'
                }`}>
                  {formData.redFlags}
                </div>
              </div>
              
              <div className="space-y-1.5 text-xs md:text-sm">
                <p className="text-[hsl(var(--text-dim))] print:text-gray-600 font-medium mb-2">
                  Critères calculés automatiquement :
                </p>
                <div className="grid grid-cols-1 gap-1">
                  <div className={`flex items-start gap-2 p-1.5 rounded ${formData.hemorragie.situations.includes('Bassin instable') ? 'bg-red-500/20 print:bg-red-100' : 'bg-[hsl(var(--bg-elev))] print:bg-gray-50'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${formData.hemorragie.situations.includes('Bassin instable') ? 'bg-red-500 text-white' : 'bg-[hsl(var(--border))] text-[hsl(var(--text-dim))]'}`}>
                      {formData.hemorragie.situations.includes('Bassin instable') ? '✓' : ''}
                    </div>
                    <span className="text-[hsl(var(--text))] print:text-gray-900">Instabilité du pelvis</span>
                  </div>
                  
                  <div className={`flex items-start gap-2 p-1.5 rounded ${formData.hemodynamique.pas && parseFloat(formData.hemodynamique.pas) < 90 ? 'bg-red-500/20 print:bg-red-100' : 'bg-[hsl(var(--bg-elev))] print:bg-gray-50'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${formData.hemodynamique.pas && parseFloat(formData.hemodynamique.pas) < 90 ? 'bg-red-500 text-white' : 'bg-[hsl(var(--border))] text-[hsl(var(--text-dim))]'}`}>
                      {formData.hemodynamique.pas && parseFloat(formData.hemodynamique.pas) < 90 ? '✓' : ''}
                    </div>
                    <span className="text-[hsl(var(--text))] print:text-gray-900">PAS &lt; 90 mmHg {formData.hemodynamique.pas && `(${formData.hemodynamique.pas})`}</span>
                  </div>
                  
                  <div className={`flex items-start gap-2 p-1.5 rounded ${formData.hemodynamique.fc && parseFloat(formData.hemodynamique.fc) > 120 ? 'bg-red-500/20 print:bg-red-100' : 'bg-[hsl(var(--bg-elev))] print:bg-gray-50'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${formData.hemodynamique.fc && parseFloat(formData.hemodynamique.fc) > 120 ? 'bg-red-500 text-white' : 'bg-[hsl(var(--border))] text-[hsl(var(--text-dim))]'}`}>
                      {formData.hemodynamique.fc && parseFloat(formData.hemodynamique.fc) > 120 ? '✓' : ''}
                    </div>
                    <span className="text-[hsl(var(--text))] print:text-gray-900">FC &gt; 120 bpm {formData.hemodynamique.fc && `(${formData.hemodynamique.fc})`}</span>
                  </div>
                  
                  <div className={`flex items-start gap-2 p-1.5 rounded ${formData.hemodynamique.pam && parseFloat(formData.hemodynamique.pam) <= 70 ? 'bg-red-500/20 print:bg-red-100' : 'bg-[hsl(var(--bg-elev))] print:bg-gray-50'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${formData.hemodynamique.pam && parseFloat(formData.hemodynamique.pam) <= 70 ? 'bg-red-500 text-white' : 'bg-[hsl(var(--border))] text-[hsl(var(--text-dim))]'}`}>
                      {formData.hemodynamique.pam && parseFloat(formData.hemodynamique.pam) <= 70 ? '✓' : ''}
                    </div>
                    <span className="text-[hsl(var(--text))] print:text-gray-900">PAM ≤ 70 mmHg {formData.hemodynamique.pam && `(${formData.hemodynamique.pam})`}</span>
                  </div>
                  
                  <div className={`flex items-start gap-2 p-1.5 rounded ${formData.respi.intubation === true ? 'bg-red-500/20 print:bg-red-100' : 'bg-[hsl(var(--bg-elev))] print:bg-gray-50'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${formData.respi.intubation === true ? 'bg-red-500 text-white' : 'bg-[hsl(var(--border))] text-[hsl(var(--text-dim))]'}`}>
                      {formData.respi.intubation === true ? '✓' : ''}
                    </div>
                    <span className="text-[hsl(var(--text))] print:text-gray-900">Intubation oro-trachéale</span>
                  </div>
                  
                  <div className={`flex items-start gap-2 p-1.5 rounded ${formData.hemodynamique.hemocue && parseFloat(formData.hemodynamique.hemocue) <= 13 ? 'bg-red-500/20 print:bg-red-100' : 'bg-[hsl(var(--bg-elev))] print:bg-gray-50'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${formData.hemodynamique.hemocue && parseFloat(formData.hemodynamique.hemocue) <= 13 ? 'bg-red-500 text-white' : 'bg-[hsl(var(--border))] text-[hsl(var(--text-dim))]'}`}>
                      {formData.hemodynamique.hemocue && parseFloat(formData.hemodynamique.hemocue) <= 13 ? '✓' : ''}
                    </div>
                    <span className="text-[hsl(var(--text))] print:text-gray-900">Hémoglobine ≤ 13 g/dL {formData.hemodynamique.hemocue && `(${formData.hemodynamique.hemocue})`}</span>
                  </div>
                </div>
              </div>
              
              {formData.redFlags > 0 && (
                <Alert className="mt-3 bg-red-500/20 print:bg-red-100 border-red-500/40 print:border-red-400 p-2">
                  <AlertTriangle className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-400 print:text-red-700" />
                  <AlertDescription className="text-[10px] md:text-xs text-red-300 print:text-red-900 font-medium">
                    <strong>ATTENTION :</strong> {formData.redFlags} critère{formData.redFlags > 1 ? 's' : ''} de gravité identifié{formData.redFlags > 1 ? 's' : ''} - Prise en charge immédiate requise
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* TRANSPORT */}
          <Card className="border-cyan-400/30 bg-cyan-400/5 print:bg-cyan-50 print:border-cyan-300 print:break-inside-avoid">
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-cyan-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-cyan-300 print:text-cyan-800 flex items-center gap-2">
                <Activity className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                TRANSPORT
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4 space-y-3">
              {/* Délai de transport */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900">
                    Délai de transport
                  </label>
                  <Badge className="bg-cyan-500/20 text-cyan-300 print:bg-cyan-200 print:text-cyan-900 font-mono text-xs md:text-sm">
                    {formData.transport.delai} min
                  </Badge>
                </div>
                <Slider
                  value={[formData.transport.delai]}
                  onValueChange={([value]) => updateField('transport', 'delai', value)}
                  min={0}
                  max={1440}
                  step={5}
                  className="print:hidden"
                />
                <div className="flex justify-between text-[10px] text-[hsl(var(--text-dim))] print:text-gray-500 mt-1">
                  <span>0 min</span>
                  <span>24h</span>
                </div>
              </div>

              {/* Date/heure d'arrivée */}
              <div>
                <label className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 mb-1.5 flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  Date/heure d'arrivée prévue
                </label>
                <Input
                  type="datetime-local"
                  value={formData.transport.dateHeureArrivee}
                  onChange={(e) => updateField('transport', 'dateHeureArrivee', e.target.value)}
                  className="h-8 md:h-9 text-xs md:text-sm bg-[hsl(var(--bg-elev))] print:bg-white"
                  data-testid="arrival-datetime-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI ANALYSIS RESULTS */}
        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5 print:bg-purple-50 print:border-purple-300 print:break-inside-avoid">
            <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-purple-100 p-2.5 md:p-4">
              <CardTitle className="text-sm md:text-base text-purple-300 print:text-purple-800 flex items-center gap-2">
                <Sparkles className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
                ANALYSE IA - Aide à la Décision
              </CardTitle>
              <p className="text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mt-1">
                Généré le {new Date(aiAnalysis.timestamp).toLocaleString('fr-FR')}
              </p>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4 p-2.5 md:p-4">
              <div className="prose prose-sm md:prose max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 leading-relaxed">
                  {aiAnalysis.analysis}
                </div>
              </div>
              
              {/* Patient Summary */}
              {aiAnalysis.patient_summary && (
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] print:border-gray-300">
                  <h3 className="text-xs md:text-sm font-semibold text-purple-300 print:text-purple-800 mb-2">Indicateurs clés</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 rounded bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-200 text-center">
                      <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600">Glasgow</div>
                      <div className="text-lg md:text-xl font-bold font-mono text-green-300 print:text-green-700">
                        {aiAnalysis.patient_summary.glasgow || '-'}
                      </div>
                    </div>
                    <div className="p-2 rounded bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-200 text-center">
                      <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600">Shock Index</div>
                      <div className="text-lg md:text-xl font-bold font-mono text-cyan-300 print:text-cyan-700">
                        {aiAnalysis.patient_summary.shock_index || '-'}
                      </div>
                    </div>
                    <div className="p-2 rounded bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-200 text-center">
                      <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600">Red Flags</div>
                      <div className="text-lg md:text-xl font-bold font-mono text-red-300 print:text-red-700">
                        {aiAnalysis.patient_summary.red_flags || 0}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Alert className="mt-4 bg-amber-400/10 border-amber-400/30 print:bg-amber-100 print:border-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 md:h-4 md:w-4 text-amber-400 print:text-amber-700" />
                <AlertDescription className="text-[10px] md:text-xs text-[hsl(var(--text))] print:text-gray-900">
                  <strong>Disclaimer :</strong> Cette analyse IA est une aide à la décision. Elle ne remplace pas l'évaluation clinique et la décision médicale.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Print Footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-600">
        <p>Bilan médical du traumatisé sévère - UrgenceMed</p>
        <p>Document généré le {new Date().toLocaleString('fr-FR')}</p>
      </div>
    </div>
  );
}
